import { Box, Card, Grid, styled, Typography } from '@mui/material';
import {
    Bar,
    BarChart,
    Cell,
    DefaultTooltipContent,
    Pie,
    PieChart,
    Rectangle,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { useEffect, useState } from 'react';
import {
    SMMedium,
    SMRegular,
    TextLGSemiBold,
    TwoXRegular
} from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@/utils/formatting';
import { EmployeeDetailResponse } from '@/services/ReportsInternalTypes';
import { Brand600 } from '@/utils/colors';
import dayjs from 'dayjs';

const StyledCard = styled(Card)`
    padding: 24px 16px 24px 16px;
    gap: 8px;
    border-radius: 8px 0px 0px 0px;
    border: 1px 0px 0px 0px;
    opacity: 0px;
    border: 1px solid #eaecf0;
    box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.4);
`;

const NumberOfEmployee = styled(Box)`
    background-color: #1570ef;
    border-radius: 10px;
    height: 273px;
    color: white;
`;

const CountText = styled(Typography)`
    font-size: 18px;
    font-weight: 700;
    line-height: 21.78px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
`;

const CountDescription = styled('span')`
    display: inline;
    font-size: 18px;
    font-weight: 400;
    line-height: 21.78px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
`;

const ChartContainer = styled(Box)`
    padding: 0px 0px 0px 0px;
    gap: 8px;
    border-radius: 4px 0px 0px 0px;
    border: 1px 0px 0px 0px;
    opacity: 0px;
    border: 1px solid #eaecf0;
`;

const ChartHeader = styled(TextLGSemiBold)`
    //padding: 0px 12px 12px 0px;
`;

const ChartSubtitle = styled(SMRegular)`
    padding: 0px 12px 12px 0px;
    //styleName: Text sm/Regular;
    font-family: Inter;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
`;

const SicknessPercentage = styled(Typography)`
    font-family: Inter;
    font-size: 72px;
    font-weight: 400;
    line-height: 90px;
    letter-spacing: -0.02em;
    text-align: center;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
`;

const COLORS = ['#53B1FD', '#F97066'];

const COLORS2 = ['#EFF8FF', '#1570EF'];

const renderTitleWithSign = ({ x, y, width, value }: any) => {
    const sign = value > 0 ? '+' : '';
    const yOffset = value < 0 ? 30 : -10;

    let color = '#D92D20';

    return (
        <>
            <text
                x={x + width / 2}
                y={y + yOffset}
                fill={color}
                fontSize={10}
                textAnchor="middle"
                dy={-6}
                fontWeight="bold"
            >
                {value == 0 ? '' : sign + value}
            </text>
        </>
    );
};

const renderTitleWithSign2 = ({ x, y, width, value, index, height }: any) => {
    const sign = value > 0 ? '+' : '';
    const yOffset = value < 0 ? 30 : -10;

    let color = '#089855';

    return (
        <>
            <text
                x={x + width / 2}
                y={132}
                fill={'#667085'}
                textAnchor="middle"
                fontSize={10}
                fontWeight={400}
            >
                {index + 1}
            </text>
            <text
                x={x + width / 2}
                y={y + yOffset}
                fill={color}
                textAnchor="middle"
                dy={-6}
                fontSize={10}
                fontWeight="bold"
            >
                {value == 0 ? '' : sign + value}
            </text>
        </>
    );
};

const ColorFill = (props: any) => {
    const { dataKey } = props;

    let color = '#089855';
    if (dataKey == 'churnedCount') {
        color = '#D92D20';
    }
    return (
        <>
            {dataKey == 'churnedCount' && (
                <>
                    <Rectangle
                        {...props}
                        y={props.y + 10}
                        label={renderTitleWithSign}
                        dataKey="uv"
                        fill={color}
                    />
                </>
            )}

            {dataKey == 'hiredCount' && (
                <Rectangle
                    {...props}
                    label={renderTitleWithSign2}
                    dataKey="uv"
                    y={props.y - 10}
                    fill={color}
                />
            )}
        </>
    );
};

const ColorFill2Male = (props: any) => {
    let color = '#53B1FD';
    return <Rectangle {...props} dataKey="uv" fill={color}></Rectangle>;
};

const ColorFill2Female = (props: any) => {
    const { dataKey } = props;

    let color = '#F97066';

    return <Rectangle {...props} dataKey="uv" fill={color}></Rectangle>;
};

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    ...props
}: any) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
        <text
            x={x}
            y={y}
            fill={'#fff'}
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
            fontWeight={'bold'}
        >
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

interface SicknessDashboardChartsProps {
    getEmployeeDetailsData?: EmployeeDetailResponse;
    startDate: Date;
    endDate: Date;
    timeFilter?: string;
}

const CustomToolTip = ({ ...props }: any) => {
    return <DefaultTooltipContent {...props} itemStyle={{ color: 'black' }} />;
};

export const SicknessDashboardCharts = ({
    getEmployeeDetailsData,
    startDate,
    endDate,
    timeFilter
}: SicknessDashboardChartsProps) => {
    const [employeeChurn, setEmployeeChurn] = useState<any[]>([]);
    const [employeeGenderRatio, setEmployeeGenderRatio] = useState<any[]>([]);

    const [employeeSickDaysByAge, setEmployeeSickDaysByAge] = useState<any[]>(
        []
    );

    useEffect(() => {
        if (getEmployeeDetailsData) {
            setEmployeeChurn(
                getEmployeeDetailsData.employmentDates.map((el) => ({
                    name: el.date,
                    churnedCount: -1 * el.churnedCount,
                    hiredCount: el.hiredCount
                }))
            );

            setEmployeeGenderRatio([
                {
                    value: getEmployeeDetailsData?.employeeGender
                        .malePercentage,
                    name: 'Male'
                },
                {
                    value: getEmployeeDetailsData?.employeeGender
                        .femalePercentage,
                    name: 'Female'
                }
            ]);

            setEmployeeSickDaysByAge(
                getEmployeeDetailsData?.sickDaysByAge.map((el) => ({
                    description: el.description,
                    workedDays: el.totalWorkedDays,
                    sickDays: el.totalSickDays,
                    totalDays: el.totalEmployees
                }))
            );
        }
    }, [getEmployeeDetailsData]);

    const { t } = useTranslation();

    const { toDecimal } = useFormatting();

    const renderTitleForSickDaysByAgeUpperText = ({
        x,
        y,
        width,
        index
    }: any) => {
        return (
            <>
                <text
                    fontWeight={'bold'}
                    x={x + width / 2}
                    y={y}
                    fill={'#000'}
                    fontSize={10}
                    textAnchor="middle"
                    dy={-6}
                    z={100}
                >
                    {toDecimal(
                        getEmployeeDetailsData?.sickDaysByAge[index]
                            .totalSickDays,
                        2
                    )}
                </text>
            </>
        );
    };

    const renderTitle = ({ x, y, width, height, index }: any) => {
        return (
            <>
                {(getEmployeeDetailsData?.employeeGenderByAge[index]
                    .maleCount ?? 0) > 10 && (
                    <text
                        //fontWeight={'bold'}
                        x={x + width / 2}
                        y={y + height / 2 + 10}
                        fontSize={10}
                        fill="#fff"
                        textAnchor="middle"
                        dy={-6}
                        z={100}
                    >
                        {toDecimal(
                            getEmployeeDetailsData?.employeeGenderByAge[index]
                                .maleCount
                        )}
                    </text>
                )}
            </>
        );
    };

    const renderUpperTitle = ({ x, y, width, height, index }: any) => {
        return (
            <>
                <>
                    <text
                        fontWeight={'bold'}
                        x={x + width / 2}
                        y={y}
                        fill="#000"
                        textAnchor="middle"
                        dy={-6}
                        fontSize={10}
                        z={100}
                    >
                        {toDecimal(
                            getEmployeeDetailsData?.employeeGenderByAge[index]
                                .totalEmployees
                        )}
                    </text>

                    {(getEmployeeDetailsData?.employeeGenderByAge[index]
                        .femaleCount ?? 0) > 20 && (
                        <text
                            x={x + width / 2}
                            y={y + height / 2 + 10}
                            fill="#fff"
                            textAnchor="middle"
                            dy={-6}
                            z={100}
                            fontSize={10}
                        >
                            {toDecimal(
                                getEmployeeDetailsData?.employeeGenderByAge[
                                    index
                                ].femaleCount
                            )}
                        </text>
                    )}
                </>
            </>
        );
    };

    const renderNumberOfEmployeeUpperTitle = ({ x, y, width, index }: any) => {
        return (
            <>
                <>
                    <text
                        fontWeight={'bold'}
                        x={x + width / 2}
                        y={y}
                        fill="#000"
                        textAnchor="middle"
                        dy={-6}
                        fontSize={10}
                        z={100}
                    >
                        {toDecimal(
                            getEmployeeDetailsData?.employeesPerMonth[index]
                                .employeeCount,
                            0
                        )}
                    </text>
                </>
            </>
        );
    };

    const totalWorkDays = getEmployeeDetailsData?.sickDays?.totalWorkedDays
        ? getEmployeeDetailsData.sickDays.totalWorkedDays
        : 1;

    return (
        <Box>
            <Grid container>
                <Grid container gap={4}>
                    <Grid item md={4}>
                        <NumberOfEmployee>
                            <TextLGSemiBold
                                style={{
                                    padding: '24px 0 0 24px'
                                }}
                            >
                                {t('number_of_employees')}
                            </TextLGSemiBold>
                            <TwoXRegular px={3} py={1}>
                                {getEmployeeDetailsData?.totalEmployees}
                            </TwoXRegular>
                            <CountText px={3}>
                                {getEmployeeDetailsData?.totalNewHires}{' '}
                                <CountDescription>
                                    {t('new_employments')}
                                </CountDescription>
                            </CountText>
                            <CountText px={3}>
                                {getEmployeeDetailsData?.totalChurned}{' '}
                                <CountDescription>
                                    {t('resignations')}
                                </CountDescription>
                            </CountText>
                        </NumberOfEmployee>
                    </Grid>
                    <Grid item md={7.5}>
                        <StyledCard
                            style={{
                                height: '273px'
                            }}
                        >
                            <ChartHeader pb={1}>
                                {t('monthly_number_of_employees')}
                            </ChartHeader>

                            <ChartContainer
                                style={{
                                    height: '189px'
                                }}
                            >
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        width={500}
                                        height={300}
                                        data={
                                            getEmployeeDetailsData?.employeesPerMonth ??
                                            []
                                        }
                                        margin={{
                                            top: 20,
                                            right: 30,
                                            left: 20,
                                            bottom: 5
                                        }}
                                    >
                                        <XAxis
                                            fontWeight={'bold'}
                                            dataKey="month"
                                        />
                                        <Tooltip content={<CustomToolTip />} />
                                        <Bar
                                            label={
                                                renderNumberOfEmployeeUpperTitle
                                            }
                                            dataKey="employeeCount"
                                            stackId="a"
                                            fill="#1570EF"
                                            //  shape={<ColorFill2Male />}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartContainer>
                        </StyledCard>
                    </Grid>
                    {(timeFilter == 'currentYear' ||
                        timeFilter == 'lastYear') && (
                        <Grid item md={11.8}>
                            <StyledCard
                                style={{
                                    height: '360px'
                                }}
                            >
                                <ChartHeader>
                                    {t('daily_employment_and_resignation')}
                                </ChartHeader>
                                <ChartSubtitle>
                                    {t('last_year_description', {
                                        startDate:
                                            dayjs(startDate).format('YYYY')
                                    })}
                                </ChartSubtitle>
                                <ChartContainer
                                    style={{
                                        height: '260px'
                                    }}
                                >
                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >
                                        <BarChart
                                            stackOffset="sign"
                                            data={employeeChurn}
                                        >
                                            <ReferenceLine stroke="#000" />
                                            <XAxis hide dataKey="name" />
                                            <YAxis hide domain={[-100, 100]} />
                                            <Tooltip
                                                content={<CustomToolTip />}
                                            />
                                            <Bar
                                                label={renderTitleWithSign}
                                                dataKey="churnedCount"
                                                stackId={'a'}
                                                shape={<ColorFill />}
                                            />
                                            <Bar
                                                label={renderTitleWithSign2}
                                                dataKey="hiredCount"
                                                stackId={'a'}
                                                shape={<ColorFill />}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </ChartContainer>
                            </StyledCard>
                        </Grid>
                    )}
                    <Grid item md={4}>
                        <StyledCard
                            style={{
                                height: '352px'
                            }}
                        >
                            <ChartHeader>{t('employee_gender')}</ChartHeader>
                            <ChartSubtitle>
                                {t('proportion_by_gender')}
                            </ChartSubtitle>
                            <ChartContainer
                                style={{
                                    height: '252px'
                                }}
                            >
                                <ResponsiveContainer width="100%" height="85%">
                                    <PieChart width={200} height={200}>
                                        <Pie
                                            data={employeeGenderRatio}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={renderCustomizedLabel}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {employeeGenderRatio.map(
                                                (entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={
                                                            COLORS[
                                                                index %
                                                                    COLORS.length
                                                            ]
                                                        }
                                                    />
                                                )
                                            )}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <SMMedium align="center">
                                    <b>
                                        {
                                            getEmployeeDetailsData
                                                ?.employeeGender?.maleCount
                                        }
                                    </b>{' '}
                                    male and{' '}
                                    <b>
                                        {
                                            getEmployeeDetailsData
                                                ?.employeeGender?.femaleCount
                                        }
                                    </b>{' '}
                                    female employees
                                </SMMedium>
                            </ChartContainer>
                        </StyledCard>
                    </Grid>

                    <Grid item md={7.5}>
                        <StyledCard
                            style={{
                                height: '352px'
                            }}
                        >
                            <ChartHeader>
                                {t('employee_age_diversity')}
                            </ChartHeader>
                            <ChartSubtitle>
                                {t('distribution_of_employee_gender')}
                            </ChartSubtitle>
                            <ChartContainer
                                style={{
                                    height: '252px'
                                }}
                            >
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        width={500}
                                        height={300}
                                        data={
                                            getEmployeeDetailsData?.employeeGenderByAge ??
                                            []
                                        }
                                        margin={{
                                            top: 20,
                                            right: 30,
                                            left: 20,
                                            bottom: 5
                                        }}
                                    >
                                        <XAxis
                                            fontWeight={'bold'}
                                            dataKey="description"
                                        />

                                        <Tooltip content={<CustomToolTip />} />
                                        <Bar
                                            label={renderTitle}
                                            dataKey="femaleCount"
                                            stackId="a"
                                            fill="#F97066"
                                            shape={<ColorFill2Female />}
                                        />
                                        <Bar
                                            label={renderUpperTitle}
                                            dataKey="maleCount"
                                            stackId="a"
                                            fill="#53B1FD"
                                            shape={<ColorFill2Male />}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartContainer>
                        </StyledCard>
                    </Grid>
                    <Grid item md={4}>
                        <StyledCard
                            style={{
                                height: '352px'
                            }}
                        >
                            <ChartHeader>{t('sick_days')}</ChartHeader>
                            <ChartSubtitle>
                                {t('percentage_sick_days')}
                            </ChartSubtitle>

                            <ChartContainer
                                style={{
                                    height: '236px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                }}
                            >
                                <SicknessPercentage color={Brand600}>
                                    {toDecimal(
                                        ((getEmployeeDetailsData?.sickDays
                                            ?.totalSickDays ?? 0) /
                                            totalWorkDays) *
                                            100,
                                        2
                                    )}
                                    %
                                </SicknessPercentage>
                                <SMMedium align="center">
                                    <b>
                                        {toDecimal(
                                            getEmployeeDetailsData?.sickDays
                                                ?.totalSickDays,
                                            0
                                        )}{' '}
                                    </b>
                                    sick days were reported out of{' '}
                                    <b>
                                        {toDecimal(
                                            getEmployeeDetailsData?.sickDays
                                                ?.totalWorkedDays,
                                            0
                                        )}{' '}
                                    </b>{' '}
                                    workdays.
                                </SMMedium>
                            </ChartContainer>
                        </StyledCard>
                    </Grid>
                    <Grid item md={7.5}>
                        <StyledCard
                            style={{
                                height: '352px'
                            }}
                        >
                            <ChartHeader>{t('sick_days')}</ChartHeader>
                            <ChartSubtitle>
                                {t('distribution_of_sick_days')}
                            </ChartSubtitle>
                            <ChartContainer
                                style={{
                                    height: '236px'
                                }}
                            >
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        width={500}
                                        height={300}
                                        data={employeeSickDaysByAge}
                                        margin={{
                                            top: 20,
                                            right: 30,
                                            left: 20,
                                            bottom: 5
                                        }}
                                    >
                                        <XAxis
                                            fontWeight={'bold'}
                                            dataKey="description"
                                        />
                                        <Tooltip content={<CustomToolTip />} />
                                        <Bar
                                            label={
                                                renderTitleForSickDaysByAgeUpperText
                                            }
                                            dataKey="sickDays"
                                            fill="#1570EF"
                                            stackId={'a'}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartContainer>
                        </StyledCard>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};
